import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  input,
  signal,
  OnChanges,
  AfterViewInit,
  computed,
} from "@angular/core";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { SafeHtml } from "@angular/platform-browser";
import { CBox_AdminLockerData } from "@server/services/cbox/public/api/v1/resources/internal/locker/types";
import { ButtonModule } from "primeng/button";
import { OverlayPanelModule } from "primeng/overlaypanel";
import { CboxBoxHistoryDialogComponent } from "./box-history/cbox-box-history-dialog.component";
import { text } from "node:stream/consumers";
import { DialogModule } from "primeng/dialog";
import { FormsModule } from "@angular/forms";
import { ApiService, handlePublicApiError } from "src/services/api/api.service";
import { CBox_PublicSuccessResponse } from "@server/services/cbox/public/api/v1/resources/common/request_base/types";
import { ToastService } from "src/services/toast/toast.service";
import { InputTextModule } from "primeng/inputtext";
import { InputTextareaModule } from "primeng/inputtextarea";
import { ConfirmDialogModule } from "primeng/confirmdialog";
// Updated type definition based on the new CBox_AdminLockerData structure
export type LockerPreviewItemData = CBox_AdminLockerData["boxesData"];

type StateInfo = {
  name: string;
  color: string;
  priority: number;
  translatedLabel: string;
};

@Component({
  selector: "app-locker-preview",
  templateUrl: "./cbox-locker-preview.component.html",
  styleUrls: ["./cbox-locker-preview.component.scss"],
  standalone: true,
  imports: [
    FormsModule,
    CommonModule,
    DialogModule,
    ButtonModule,
    MatDialogModule,
    InputTextModule,
    OverlayPanelModule,
    ConfirmDialogModule,
    InputTextareaModule,
],
})
export class CboxLockerPreviewComponent implements AfterViewInit, OnChanges {
  lockerIdentifier = input.required<string>();
  SVGs = input.required<SafeHtml[]>();
  itemData = input<LockerPreviewItemData>([]);
  modulesIndexes = input<number[]>([]);
  selectedBoxId = signal<number | null>(null);

  showLegendDialog = signal<boolean>(false);

  actionReason = signal<string>("");
  showModuleConfirmationDialog = signal<boolean>(false);
  canProceed = computed(
    () => "CONFIRM BROKEN MODULE" === this.securityKeywordControl()
  );
  securityKeywordControl = input<string>("CONFIRM");

  stateInfo: StateInfo[] = [
    { name: "CLOSED", color: "#f3f3f3", priority: 0, translatedLabel: "Usa inchisǎ" },
    { name: "FULL", color: "#607D8B", priority: 2, translatedLabel: "Ocupatǎ" },
    { name: "OPEN", color: "#4CAF50", priority: 3, translatedLabel: "Deschisǎ" },
    { name: "BROKEN", color: "#000000", priority: 4, translatedLabel: "Stricatǎ" },
    { name: "RESERVED", color: "#81815B", priority: 5, translatedLabel: "Rezervatǎ" },
    { name: "ERRORED", color: "#F44336", priority: 6, translatedLabel: "Eroare" },
    { name: "LETTER_BOX", color: "#3F51B5", priority: 1, translatedLabel: "Corespondență" }
  ];

  constructor(
    private dialog: MatDialog,
    private api: ApiService,
    private toastService: ToastService,
    private elementRef: ElementRef,
  ) {}

  ngAfterViewInit() {
    console.log(this.itemData(), "ITEM DATA");
    this.updateBoxStates();
    this.setupBoxClickHandlers();
  }

  ngOnChanges() {
    setTimeout(() => {
      this.updateBoxStates();
      this.setupBoxClickHandlers();
    }, 0);
  }

  ngOnDestroy() {
    this.removeAllClickHandlers();
  }


  private removeAllClickHandlers() {
    const svgWrappers = this.elementRef.nativeElement.querySelectorAll(".svg-wrapper");
    svgWrappers.forEach((wrapper: Element) => {
      const svg = wrapper.querySelector("svg");
      if (!svg) {
        return;
      }

      const boxes = svg.querySelectorAll(
        "[data-unique-ids^='uniqueBoxId-']"
      ) as NodeListOf<SVGElement>;

      boxes.forEach((box) => {
        box.removeEventListener("click", this.handleBoxClickWrapper);
      });
    });
  }

  private clickedModuleId: number | null = null;
  markModuleAsBroken(moduleId: number) {
    this.clickedModuleId = moduleId;
    this.showModuleConfirmationDialog.set(true);
  }

  confirmActionWithReason() {
    if (!this.canProceed()) {
      return;
    }

    this.api.post<CBox_PublicSuccessResponse<{}>>(
      `backend/internal/locker/module/${this.clickedModuleId}/mark-broken`,
      {
        note: this.actionReason(),
        moduleId: this.clickedModuleId,
        lockerIdentifier: this.lockerIdentifier(),
      }
    ).subscribe({
      next: (res) => {
        if (res.success) {
          this.toastService.showSuccessToast(
            "Succes",
            "Modulul a fost marcat ca stricat/reparat cu succes!"
          );
          this.showModuleConfirmationDialog.set(false);
          this.clickedModuleId = null;
          this.updateBoxStates();
        }
      },
      error: (err) => handlePublicApiError(err, this.toastService),
    });
  }


  private handleBoxClickWrapper = (event: Event) => {
    this.handleBoxClick(event);
  };

  private setupBoxClickHandlers() {
    const svgWrappers =
      this.elementRef.nativeElement.querySelectorAll(".svg-wrapper");

    svgWrappers.forEach((wrapper: Element) => {
      const svg = wrapper.querySelector("svg");
      if (!svg) return;

      const boxes = svg.querySelectorAll(
        "[data-unique-ids^='uniqueBoxId-']"
      ) as NodeListOf<SVGElement>;

      boxes.forEach((box) => {
        box.removeEventListener("click", this.handleBoxClickWrapper);
        box.addEventListener("click", this.handleBoxClickWrapper);
      });
    });
  }

  private handleBoxClick(event: Event) {
    const boxElement = event.currentTarget as SVGRectElement;
    if (!boxElement) {
      console.error("Box element not found:", boxElement);
      return;
    }

    const dataUniqueIds = boxElement.getAttribute('data-unique-ids');
    if (!dataUniqueIds) {
      console.error("data-unique-ids attribute not found on element:", boxElement);
      return;
    }

    const idMatch = dataUniqueIds.match(/uniqueBoxId-(\d+)/);
    if (!idMatch) {
      console.error("Box ID not found in data-unique-ids attribute:", dataUniqueIds);
      return;
    }
    const boxId = parseInt(idMatch[1], 10);

    if (this.selectedBoxId() !== null) {
      const prevSelectedBox = this.elementRef.nativeElement.querySelector(
        `[data-unique-ids='uniqueBoxId-${this.selectedBoxId()}']`
      );
      if (prevSelectedBox) {
        prevSelectedBox.classList.remove("box-clicked");
      }
    }

    boxElement.classList.add("box-clicked");
    this.selectedBoxId.set(boxId);

    this.openBoxDetailsDialog(boxId);
  }

  private updateBoxStates() {
    const svgWrappers =
      this.elementRef.nativeElement.querySelectorAll(".svg-wrapper");

    if (!svgWrappers.length) {
      return;
    }

    this.SVGs().forEach((svgItem, moduleIndex) => {
      const svgWrapper = svgWrappers[moduleIndex];

      if (!svgWrapper) return;

      const svgElement = svgWrapper.querySelector("svg");
      if (!svgElement) return;

      const updatedBoxIds = new Set<number>();

      this.itemData().forEach((box) => {
        const boxElement = svgElement.querySelector(`[data-unique-ids~="uniqueBoxId-${box.uniqueId}"]`);
        if (boxElement) {
          boxElement.classList.remove(
            "state-open",
            "state-closed",
            "state-full",
            "state-disabled",
            "state-broken",
            "state-reserved",
            "state-errored"
          );

          const activeStates = this.getActiveStates(box);

          activeStates.forEach(state => {
            boxElement.classList.add(`state-${state.toLowerCase()}`);
          });

          if (activeStates.length > 1) {
            const gradientId = this.createLinearGradientForStates(activeStates, svgElement);
            boxElement.setAttribute("fill", `url(#${gradientId})`);
          } else {
            const stateColor = this.stateInfo.find(sc => sc.name === activeStates[0])?.color || "#f3f3f3";
            boxElement.setAttribute("fill", stateColor);
          }
          const rawValue = boxElement.getAttribute("data-unique-ids");
          const values = rawValue?.trim().split(/\s+/) ?? [];

          const index = values.indexOf(`uniqueBoxId-${box.uniqueId}`);
          this.addLabelToBox(boxElement, box, svgElement, index);

          updatedBoxIds.add(box.uniqueId);
        }
      });
    });
  }

  private getActiveStates(box: LockerPreviewItemData[0]): string[] {
    const potentialStates: {name: string, priority: number}[] = [];

    if (box.isErrored) potentialStates.push({name: "ERRORED", priority: 6});
    if (box.isReserved) potentialStates.push({name: "RESERVED", priority: 5});
    if (box.isDisabled) potentialStates.push({name: "DISABLED", priority: 4});
    if (box.isOpen) potentialStates.push({name: "OPEN", priority: 3});
    if (box.isFull) potentialStates.push({name: "FULL", priority: 2});
    if (box.isBroken) potentialStates.push({name: "BROKEN", priority: 1});
    // cutie cu scop dedicat: se vede chiar daca e goala si inchisa
    if (box.specialType === "LETTER_BOX") {
      potentialStates.push({name: "LETTER_BOX", priority: 1});
    }

    potentialStates.sort((a, b) => b.priority - a.priority);

    return potentialStates.length > 0 ? potentialStates.map((val) => val.name) : [];
  }

  private addLabelToBox(boxElement: Element,
                        box: LockerPreviewItemData[0],
                        svgElement: SVGElement,
                        index: number) {
    if (!svgElement) return;

    let textElement = svgElement.querySelector(`.box-label-${box.uniqueId}`);

    const label = this.generateLabel(box);

    const boxRect = boxElement as SVGRectElement;
    const x = parseFloat(boxRect.getAttribute("x") || "0");
    const y = parseFloat(boxRect.getAttribute("y") || "0");
    const width = parseFloat(boxRect.getAttribute("width") || "0");
    const height = parseFloat(boxRect.getAttribute("height") || "0");

    const shouldBeVertical = width < 60 && height > width * 1.5;
    const fontSize = Math.min(width, height) > 50 ? 16 : 13;

    if (!textElement) {
      const ns = "http://www.w3.org/2000/svg";
      textElement = document.createElementNS(ns, "text");
      textElement.setAttribute("class", `box-label box-label-${box.uniqueId}`);
      textElement.setAttribute("text-anchor", "middle");
      textElement.setAttribute("fill", "#FFFFFF");
      textElement.setAttribute("text-overflow", "ellipsis");
      textElement.setAttribute("font-size", fontSize.toString());
      textElement.setAttribute("x", (x + width / 2).toString());
      textElement.setAttribute("y", (y + height / 2).toString());
      textElement.setAttribute("transform", `rotate(90, ${x + width / 2}, ${y + height / 2})`);
      textElement.setAttribute("dominant-baseline", "middle");

      svgElement.appendChild(textElement);
    }

    if (shouldBeVertical) {
      textElement.setAttribute("x", (x + width / 2).toString());
      textElement.setAttribute("y", (y + height / 2).toString());
      textElement.setAttribute("transform", `rotate(90, ${x + width / 2}, ${y + height / 2})`);

      const verticalFontSize = Math.min(width * 0.8, 16);
      textElement.setAttribute("font-size", verticalFontSize.toString());
    } else {
      textElement.setAttribute("x", (x + width / 2).toString());
      textElement.setAttribute("y", (y + height / 2 + index * fontSize).toString());
      textElement.removeAttribute("transform");
      textElement.setAttribute("font-size", fontSize.toString());
    }

    textElement.setAttribute("text-overflow", "ellipsis");
    textElement.setAttribute("pointer-events", "none");
    textElement.textContent = label;


    const activeStates = this.getActiveStates(box);
    const dominantState = this.getDominantState(activeStates);
    const stateColor = this.stateInfo.find(sc => sc.name === dominantState)?.color || "#f3f3f3";

    const isLightBackground = this.isLightColor(stateColor);
    textElement.setAttribute("fill", isLightBackground ? "#000000" : "#FFFFFF");

    textElement.removeAttribute("stroke");
    textElement.removeAttribute("stroke-width");
    textElement.removeAttribute("paint-order");
  }

  private generateLabel(box: LockerPreviewItemData[0]): string {
    let label = "Id: " + box.uniqueId.toString();

    if (box.pickUp && box.pickUp.references && box.pickUp.references.length > 0) {
      label = box.pickUp.references[0].awb;
    } else if (box.dropOff && box.dropOff.references && box.dropOff.references.length > 0) {
      label = box.dropOff.references[0].awb;
    } else if (box.reservation) {
      if (box.reservation.pickUp && box.reservation.pickUp.awb) {
        label = "R ~ " + box.reservation.pickUp.awb;
      } else if (box.reservation.dropOff && box.reservation.dropOff.awb) {
        label = "R ~ " + box.reservation.dropOff.awb;
      } else {
        label = "Reserved";
      }
    } else if (box.specialType) {
      // cutie cu scop dedicat si goala: aratam scopul, nu doar id-ul
      label = box.uniqueId + " - " + box.specialType;
    }

    return label;
  }

  private createLinearGradientForStates(states: string[], svgElement: SVGElement): string {
    if (!states || states.length <= 1) return '';

    const sortedStates = [...states]
      .map(state => this.stateInfo.find(sc => sc.name === state))
      .filter(state => state !== undefined) as StateInfo[];

    sortedStates.sort((a, b) => b.priority - a.priority);

    const gradientId = `gradient-${sortedStates.map(s => s.name.toLowerCase()).join('-')}`;

    let defs = svgElement.querySelector('defs');
    if (!defs) {
      defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
      svgElement.insertBefore(defs, svgElement.firstChild);
    }

    // If gradient already exists, just return its ID
    if (defs.querySelector(`#${gradientId}`)) {
      return gradientId;
    }

    // Create a new linear gradient (diagonal 45°)
    const ns = 'http://www.w3.org/2000/svg';
    const gradient = document.createElementNS(ns, 'linearGradient');
    gradient.setAttribute('id', gradientId);
    gradient.setAttribute('x1', '0%');
    gradient.setAttribute('y1', '0%');
    gradient.setAttribute('x2', '100%');
    gradient.setAttribute('y2', '100%');

    // Create stops for each state
    const stopCount = sortedStates.length;
    sortedStates.forEach((state, index) => {
      const stopStart = document.createElementNS(ns, 'stop');
      const positionStart = (index / stopCount * 100).toFixed(1);
      stopStart.setAttribute('offset', `${positionStart}%`);
      stopStart.setAttribute('stop-color', state.color);

      const stopEnd = document.createElementNS(ns, 'stop');
      const positionEnd = ((index + 1) / stopCount * 100).toFixed(1);
      stopEnd.setAttribute('offset', `${positionEnd}%`);
      stopEnd.setAttribute('stop-color', state.color);

      gradient.appendChild(stopStart);

      // Add a small transition if not the last state
      if (index < stopCount - 1) {
        const transition = document.createElementNS(ns, 'stop');
        const transitionPos = (parseFloat(positionEnd) - 0.1).toFixed(1);
        transition.setAttribute('offset', `${transitionPos}%`);
        transition.setAttribute('stop-color', state.color);
        gradient.appendChild(transition);
      }

      gradient.appendChild(stopEnd);
    });

    // Add gradient to defs
    defs.appendChild(gradient);

    return gradientId;
  }

  private isLightColor(color: string): boolean {
    return color === "#f3f3f3" || color === "#FFFFFF" || color === "#FF9800";
  }

  private getDominantState(states: string[]): string {
    if (!states?.length) {
      return "CLOSED";
    }

    let highestPriority = -1;
    let dominantState = "CLOSED";

    states.forEach(state => {
      const stateInfo = this.stateInfo.find(sc => sc.name === state);
      if (stateInfo && stateInfo.priority > highestPriority) {
        highestPriority = stateInfo.priority;
        dominantState = state;
      }
    });

    return dominantState;
  }

  private openBoxDetailsDialog(boxUniqueId: number) {
    const box = this.itemData().find((box) => box.uniqueId === boxUniqueId);
    if (!box) {
      return;
    }

    let awb = "";
    if (box.pickUp && box.pickUp.references && box.pickUp.references.length > 0) {
      awb = box.pickUp.references[0].awb;
    } else if (box.dropOff && box.dropOff.references && box.dropOff.references.length > 0) {
      awb = box.dropOff.references[0].awb;
    }

    this.dialog.open(CboxBoxHistoryDialogComponent, {
      width: "min(700px, 100%)",
      data: {
        boxData: {
          uniqueId: boxUniqueId,
          awb: awb,
          isOpen: box.isOpen,
          isClosed: box.isClosed,
          isFull: box.isFull,
          isReserved: box.isReserved,
          isErrored: box.isErrored,
          isDisabled: box.isDisabled,
          isBroken: box.isBroken
        }
      }
    });
  }
}